import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataGrid, {
  Column,
  FilterRow,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
  Sorting,
  MasterDetail,
} from "devextreme-react/data-grid";
import type { DataGridTypes } from "devextreme-react/data-grid";
import Button from "devextreme-react/button";
import Popup from "devextreme-react/popup";
import ScrollView from "devextreme-react/scroll-view";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
import GridContainer from "./GridContainer";
import ProductDetail from "./ProductDetail";
import { Productos } from "../../../dtos/Productos";
import { ApiError } from "../../../services/apiError";
import axios, { AxiosError } from "axios";
import notify from "devextreme/ui/notify";
import { SyncProductosResponse } from "../../../dtos/SyncProductos";
import { productosService } from "../../../services/productosService";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    return {
      message:
        error.response?.data?.message || "Ocurrió un error en el servidor.",
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return {
    message: "Ocurrió un error inesperado. Revisa tu conexión de red.",
  };
};

const PAGE_SIZE = 50;

const ProductosGrid: React.FC = () => {
  const dataGridRef = useRef<any>(null);
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState<Productos[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [confirmSyncVisible, setConfirmSyncVisible] = useState(false);
  const [resultSyncVisible, setResultSyncVisible] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncProductosResponse | null>(null);

  const fetchData = useCallback(async (page = pageNumber) => {
    setLoading(true);
    try {
      const response = await productosService.getPagination(page, PAGE_SIZE);
      setDataSource(response ?? []);
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoading(false);
    }
  }, [pageNumber]);

  useEffect(() => {
    fetchData(pageNumber);
  }, [pageNumber, fetchData]);

  const handleExportExcel = async () => {
    const gridInstance = dataGridRef.current?.instance();
    if (!gridInstance) {
      notify("No se pudo obtener la tabla para exportar.", "error", 3000);
      return;
    }
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Productos");
    await exportDataGrid({
      component: gridInstance,
      worksheet,
      autoFilterEnabled: true,
    });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      "Productos.xlsx",
    );
  };

  const handleSyncProductos = async () => {
    try {
      setSyncLoading(true);
      setConfirmSyncVisible(false);
      const response = await productosService.syncProductos();
      setSyncResult(response ?? null);
      setResultSyncVisible(true);
      notify(
        response?.mensaje ||
          (response?.exito
            ? "Sincronización ejecutada."
            : "Sincronización con observaciones."),
        response?.exito ? "success" : "warning",
        3000,
      );
      await fetchData(pageNumber);
    } catch (err) {
      const errorInfo = handleApiError(err);
      setSyncResult({ exito: false, mensaje: errorInfo.message });
      setResultSyncVisible(true);
      notify(errorInfo.message, "error", 3000);
    } finally {
      setSyncLoading(false);
    }
  };

  const renderCapacidadCell = (
    cellInfo: DataGridTypes.ColumnCellTemplateData,
  ) => {
    const cap = cellInfo.data?.capacidad;
    const um = cellInfo.data?.um ?? "";
    if (cap == null || cap === "") return "";
    return um ? `${cap} ${um}` : `${cap}`;
  };

  const renderDateCell = (cellInfo: DataGridTypes.ColumnCellTemplateData) => {
    if (!cellInfo.value) return "";
    const date = new Date(cellInfo.value);
    return Number.isNaN(date.getTime())
      ? cellInfo.value
      : date.toLocaleString();
  };

  const advertencias = Array.isArray(syncResult?.advertencias)
    ? (syncResult?.advertencias ?? [])
    : [];

  return (
    <>
      <GridContainer title="Productos">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            padding: "16px 24px 12px",
            borderBottom: "1px solid #DFE1E6",
          }}
        >
          <Button
            icon="chevronleft"
            text="Regresar"
            stylingMode="outlined"
            onClick={() => navigate(-1)}
          />
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Button
              icon="refresh"
              text={syncLoading ? "Sincronizando..." : "Sincronizar"}
              stylingMode="contained"
              onClick={() => setConfirmSyncVisible(true)}
              disabled={syncLoading}
            />
            <Button
              icon="chevronprev"
              text="Anterior"
              onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              disabled={pageNumber <= 1 || loading}
            />
            <span style={{ alignSelf: "center" }}>Página {pageNumber}</span>
            <Button
              icon="chevronnext"
              text="Siguiente"
              onClick={() => setPageNumber((p) => p + 1)}
              disabled={loading || dataSource.length < PAGE_SIZE}
            />
            <Button
              icon="xlsxfile"
              text="Exportar Excel"
              stylingMode="contained"
              onClick={handleExportExcel}
            />
          </div>
        </div>

        <DataGrid
          ref={dataGridRef}
          dataSource={dataSource}
          keyExpr="codigo"
          showBorders
          columnAutoWidth
          wordWrapEnabled
          loadPanel={{ enabled: loading }}
        >
          <FilterRow visible />
          <HeaderFilter visible allowSearch />
          <Paging defaultPageSize={PAGE_SIZE} />
          <Pager showInfo />
          <Sorting mode="multiple" />
          <SearchPanel visible width={300} placeholder="Buscar productos..." />
          <MasterDetail enabled component={ProductDetail} />

          <Column dataField="codigo" caption="Código" width={110} />
          <Column dataField="descripcion" caption="Descripción" width={320} />
          <Column
            dataField="capacidad"
            caption="Capacidad"
            cellRender={renderCapacidadCell}
            width={130}
          />
          <Column dataField="um" caption="UM" width={80} />
          <Column dataField="caducidad" caption="Caducidad" width={100} />
          <Column dataField="claveRegistro" caption="Clave registro" width={140} />
          <Column dataField="lineavta" caption="Línea venta" width={110} />
          <Column dataField="codigodotacion" caption="Cód. dotación" width={120} />
          <Column
            dataField="fechaUltimaSincronizacionOrigen"
            caption="Últ. sync"
            cellRender={renderDateCell}
            width={160}
          />
          <Column
            dataField="vigenteEnFuenteOrigen"
            caption="Vigente origen"
            dataType="boolean"
            width={120}
          />
          <Column
            dataField="editadoManualmente"
            caption="Editado manual"
            dataType="boolean"
            width={130}
          />
        </DataGrid>
      </GridContainer>

      <Popup
        visible={confirmSyncVisible}
        onHiding={() => setConfirmSyncVisible(false)}
        title="Confirmar sincronización"
        width={520}
        height="auto"
      >
        <div style={{ padding: 24 }}>
          <p>¿Sincronizar productos desde el maestro de origen?</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 20 }}>
            <Button text="Cancelar" onClick={() => setConfirmSyncVisible(false)} />
            <Button
              text={syncLoading ? "Sincronizando..." : "Sí, sincronizar"}
              type="default"
              onClick={handleSyncProductos}
              disabled={syncLoading}
            />
          </div>
        </div>
      </Popup>

      <Popup
        visible={resultSyncVisible}
        onHiding={() => setResultSyncVisible(false)}
        title="Resultado de sincronización"
        width={640}
        height={480}
      >
        <ScrollView width="100%" height="100%">
          <div style={{ padding: 20 }}>
            <p><strong>Éxito:</strong> {syncResult?.exito ? "Sí" : "No"}</p>
            <p><strong>Mensaje:</strong> {syncResult?.mensaje ?? "—"}</p>
            {advertencias.length > 0 && (
              <ul>
                {advertencias.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            )}
          </div>
        </ScrollView>
      </Popup>
    </>
  );
};

export default ProductosGrid;
